import React, { useState } from 'react';

import classNames from 'classnames';
import { Badge } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBinoculars,
  faCalendar,
  faEdit,
  faEnvelope,
  faPhone,
  faStickyNote,
  faThumbtack,
  faThumbtackSlash,
  type IconDefinition
} from '@fortawesome/pro-regular-svg-icons';
import {
  faBinoculars as faBinocularsSolid,
  faCalendar as faCalendarSolid,
  faEnvelope as faEnvelopeSolid,
  faLock,
  faNoteSticky as faNoteStickySolid,
  faPhone as faPhoneSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

import confirm from 'components/confirm';
import Dropdown from 'components/dropdown';
import { Button } from 'components/ui/button';
import { activityHeaderText } from 'bundles/apex/activities/activity/helpers';
import { activityTypes } from 'bundles/apex/activities/type/config';
import { timelineTimeLabel, timelineTimeTitle } from 'bundles/apex/activities/timeline_helpers';
import { findMatchingCurrentParent, getParentLabel, getParentPath } from 'bundles/crm/parent_helpers';
import { formatDate } from 'helpers/formatters/date_time_formats';
import { post } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';
import Avatars from 'bundles/connect/components/profile/avatars';
import ActivityParent from './parent';
import ActivityEmailThread from 'bundles/apex/activities/activity/email_thread';
import ActivityBody from 'bundles/apex/activities/activity/body';
import ActivityModal from 'bundles/apex/activities/modal';
import LogActivityModal from 'bundles/apex/activities/log_activity/modal';
import SendEmailModal from 'bundles/apex/activities/send_email/modal';
import ReauthModal from 'bundles/apex/activities/send_email/reauth_modal';
import TaskFormModal from 'bundles/apex/tasks/task_modal';
import ReparentingMenuItems from 'bundles/crm/parents/reparenting_menu_items';
import { deleteActivity, dismissActivity } from 'bundles/apex/activities/form/helpers';
import { refreshActivities } from 'bundles/apex/activities/hooks';

import type { Activity, MenuOptions, TaskConfig } from 'bundles/apex/activities/types';
import { clearPendingReply, storePendingReply } from 'bundles/apex/activities/send_email/helpers';

import type { PendingReply, ReplyMode, SendEmailConfig } from 'bundles/apex/activities/send_email/types';
import type { Task, TaskAttributes } from 'bundles/apex/tasks/types';
import type { Parent, ParentOption } from 'bundles/crm/types';

import './styles.scss';

type ActivityItemProps = {
  activity: Activity;
  showParents?: boolean | 'path';
  showActions?: boolean;
  aiEnabled?: boolean;
  currentParent?: Parent | Parent[];
  options?: {
    parents?: ParentOption[];
  };
  menuOptions?: MenuOptions;
  autoExpandEmails?: boolean;
  hideAfterReparenting?: boolean;
  forUserId?: number;
  isBackofficeAdmin?: boolean;
  showPinned?: boolean;
  showRedesignModal?: boolean;
  showNeedsReply?: boolean;
  subjectName?: string;
  tagText?: string;
  sendEmail?: SendEmailConfig;
  taskConfig?: TaskConfig;
  // The contact timeline's lighter row: actor › subject line, relative time, hover actions
  timeline?: boolean;
  resumeReply?: PendingReply | null;
  onDelete?: (activity: Activity) => void;
  onResumeConsumed?: () => void;
  onSave?: (activity: Activity) => void;
  onTogglePin?: (activity: Activity) => void;
}

const timelineIcons: Record<string, IconDefinition> = {
  call: faPhoneSolid,
  meeting: faCalendarSolid,
  message: faEnvelopeSolid,
  note: faNoteStickySolid,
  showing: faBinocularsSolid,
};

export default function ActivityItem({
  activity,
  aiEnabled,
  currentParent,
  options,
  menuOptions,
  autoExpandEmails,
  hideAfterReparenting,
  showParents,
  showActions = true,
  showPinned,
  showRedesignModal,
  showNeedsReply,
  subjectName,
  sendEmail,
  taskConfig,
  timeline,
  resumeReply,
  onResumeConsumed,
  forUserId,
  isBackofficeAdmin,
  tagText,
  onDelete,
  onSave,
  onTogglePin
}: ActivityItemProps) {
  if (!activity) return null;

  const [showEditModal, setShowEditModal] = useState(false);
  const [replyMode, setReplyMode] = useState<ReplyMode | null>(null);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const header = activityHeaderText(activity);
  let icon: IconDefinition | null = null;
  let canEdit = !activity.hasCrmEmail;
  let canDelete = true;

  let isCreator = false;
  if (forUserId != null) {
    isCreator = forUserId === activity.creator.id;
    canEdit = isCreator && !activity.hasCrmEmail;
    canDelete = isCreator || Boolean(isBackofficeAdmin);
  }

  const handleDelete = (event) => {
    event.stopPropagation();

    let title, message;
    if (activity.isEmailThread) {
      title = 'Delete Thread?';
      message = 'Are you sure you want to delete this thread and all of its messages?';
    } else {
      title = 'Delete Activity?';
      message = 'Are you sure you want to delete this activity?';
    }

    confirm(message, {
      buttonLabel: 'Delete',
      buttonStyle: 'danger',
      title: title
    }).then(async () => {
      const deletedActivity = await deleteActivity(activity);
      if (onDelete) {
        onDelete(deletedActivity);
      }
    }).catch(() => { });
  };

  switch (activity.activityType) {
  case 'call':
    icon = faPhone;
    break;
  case 'message':
    icon = faEnvelope;
    break;
  case 'meeting':
    icon = faCalendar;
    break;
  case 'showing':
    icon = faBinoculars;
    break;
  case 'note':
    icon = faStickyNote;
    break;
  default:
    icon = faEnvelope;
  }

  const needsReply = Boolean(activity.needsReply && showNeedsReply && showActions);
  const canReply = Boolean(needsReply && sendEmail?.enabled && sendEmail.path && activity.replyTo?.to?.length);
  const mustReauthToReply = canReply && !sendEmail?.canSend;

  const startReply = (mode: ReplyMode) => {
    if (!mustReauthToReply) {
      clearPendingReply();
      setReplyMode(mode);
      return;
    }

    if (sendEmail?.contactId) {
      storePendingReply(activity.id, mode, sendEmail.contactId);
    }
    setShowReauthModal(true);
  };

  const closeReauth = () => {
    clearPendingReply();
    setShowReauthModal(false);
  };

  const resumedMode = resumeReply?.activityId === activity.id ? resumeReply.mode : null;
  const activeReplyMode = replyMode ?? resumedMode;

  const closeReply = () => {
    clearPendingReply();
    setReplyMode(null);
    onResumeConsumed?.();
  };

  async function dismiss({ notifyOnError = false, refresh = true } = {}) {
    try {
      const saved: Activity = await dismissActivity(activity);
      onSave?.(saved);

      if (refresh) refreshActivities();
    } catch {
      if (notifyOnError) showSnackbar({ text: 'Something went wrong. ', variant: 'danger' });
    }
  }

  const replyTask: Task = {
    name: `Reply to ${activity.replyTo?.contactName || subjectName || 'contact'}`,
    attachments: [],
    formDependenciesPath: taskConfig?.formDependenciesPath || '',
    reminders: [],
  };

  const createReplyTask = (task: TaskAttributes): Promise<Task> => {
    return post(taskConfig?.createPath || '', { assigneeId: taskConfig?.forUser.id, ...task });
  };

  const needsReplyActions = needsReply && (
    <div className="d-flex flex-wrap align-items-center gap-2">
      {canReply && (
        <>
          <Button size="sm" variant="primary" onClick={() => startReply('reply')}>Reply</Button>
          <Button size="sm" variant="outline" onClick={() => startReply('replyAll')}>Reply all</Button>
        </>
      )}
      {taskConfig && (
        <Button size="sm" variant="outline" onClick={() => setShowTaskModal(true)}>Task for later</Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => dismiss({ notifyOnError: true })}>Dismiss</Button>
    </div>
  );

  const menu = showActions && (
    <Dropdown className={classNames({ 'pt-2': !timeline })} size="sm">
      <Dropdown.Item disabled={!canEdit} onClick={() => setShowEditModal(true)}>
        <FontAwesomeIcon icon={faEdit} />
        Edit Activity
      </Dropdown.Item>
      {showPinned && (
        <Dropdown.Item onClick={() => onTogglePin?.(activity)}>
          <FontAwesomeIcon icon={activity.pinned ? faThumbtackSlash : faThumbtack} />
          {activity.pinned ? 'Unpin Activity' : 'Pin Activity'}
        </Dropdown.Item>
      )}
      {isCreator &&
        <ReparentingMenuItems
          parentOptions={menuOptions?.parents}
          record={activity}
          onReparent={activity => hideAfterReparenting ? onDelete?.(activity) : onSave?.(activity)}
        />
      }
      <Dropdown.Item disabled={!canDelete} isDelete onClick={handleDelete} />
    </Dropdown>
  );

  const modals = (
    <>
      {showActions && (
        showRedesignModal && activity.path ? (
          <LogActivityModal
            activity={activity}
            aiEnabled={aiEnabled}
            parentId={activity.parentId}
            parentType={activity.parentType}
            path={activity.path}
            show={showEditModal}
            subjectName={subjectName}
            onHide={() => setShowEditModal(false)}
            onLogged={savedActivity => onSave?.(savedActivity)}
          />
        ) : (
          <ActivityModal
            activity={activity}
            header="Edit activity"
            show={showEditModal}
            tagText={tagText}
            onHide={() => setShowEditModal(false)}
            onSave={onSave}
          />
        )
      )}

      {activeReplyMode && sendEmail?.path && activity.replyTo && (
        <SendEmailModal
          cc={activeReplyMode === 'replyAll' ? activity.replyTo.cc : []}
          initialSubject={activity.replyTo.subject}
          path={sendEmail.path}
          to={activity.replyTo.to}
          onClose={closeReply}
          onSend={() => dismiss({ refresh: false })}
        />
      )}

      {showReauthModal && sendEmail?.contactId && (
        <ReauthModal
          authPath={sendEmail.authPath}
          connected={sendEmail.connected}
          contactId={sendEmail.contactId}
          providerName={sendEmail.providerName}
          onClose={closeReauth}
        />
      )}

      {showTaskModal && taskConfig && (
        <TaskFormModal
          createTask={createReplyTask}
          disableAssignee={taskConfig.disableAssignee}
          formDependenciesPath={taskConfig.formDependenciesPath}
          forUser={taskConfig.forUser}
          show
          showAdditionalOptions
          task={replyTask}
          onHide={() => setShowTaskModal(false)}
          onSave={() => {
            dismiss();
            setShowTaskModal(false);
          }}
        />
      )}
    </>
  );

  if (timeline) {
    const onCurrentRecord = findMatchingCurrentParent(activity, currentParent);
    const parentLabel = getParentLabel(activity, { currentParent, parentOptions: options?.parents });
    const parentPath = showParents === 'path' ? getParentPath(activity, { currentParent }) : null;
    const isPrivate = Boolean(onCurrentRecord && parentLabel === 'Private');

    // A received email reads from the contact to the broker; everything else from the broker
    const inbound = activity.isEmailThread && !activity.sentByUser && subjectName;
    const actor = inbound ? subjectName : activity.creator.name;
    const recipient = inbound ? activity.creator.name : subjectName;
    const typeConfig = activityTypes[activity.activityType];
    const phrase: string = typeConfig?.phrase || 'logged an activity';
    const subject = activity.isEmailThread
      ? activity.emails?.[0]?.subject || header
      : phrase.charAt(0).toUpperCase() + phrase.slice(1);

    return (
      <article className="activity-row">
        <div className="activity-row__rail">
          <span className="activity-row__icon-wrap">
            <span className={classNames('activity-row__icon', { 'activity-row__icon--attention': needsReply })}>
              <FontAwesomeIcon icon={timelineIcons[activity.activityType] || faEnvelopeSolid} />
            </span>
          </span>
          <span aria-hidden="true" className="activity-row__connector" />
        </div>

        <div className="activity-row__body">
          <div className="activity-row__head">
            <div className="activity-row__actors">
              <span className="fw-semibold">{actor}</span>
              {recipient && (
                <>
                  <span aria-hidden="true">›</span>
                  <span>{recipient}</span>
                </>
              )}
              <span className="activity-row__meta">
                <Tooltip>
                  <Tooltip.Trigger delay={0} render={<span className="text-nowrap" />}>
                    {timelineTimeLabel(activity)}
                  </Tooltip.Trigger>
                  <Tooltip.Content>{timelineTimeTitle(activity)}</Tooltip.Content>
                </Tooltip>
                {isPrivate && (
                  <span className="d-inline-flex align-items-center gap-1 text-nowrap">
                    <FontAwesomeIcon icon={faLock} />
                    Private
                  </span>
                )}
              </span>
            </div>
            <div className="activity-row__context">
              <span className="activity-row__subject">{subject}</span>
              {parentPath && parentLabel && (
                <span className="activity-row__assoc">
                  <span className="activity-row__assoc-separator">|</span>
                  <a className="activity-row__parent-link" href={parentPath}>{parentLabel}</a>
                </span>
              )}
            </div>
          </div>

          <div className="activity-row__content">
            {activity.isEmailThread
              ? <ActivityEmailThread activity={activity} autoExpand={autoExpandEmails} />
              : <ActivityBody activity={activity} timeline />
            }
          </div>

          {needsReplyActions}
        </div>

        {showActions && (
          <div className="activity-row__actions">
            {showPinned && (
              <Button
                aria-label={activity.pinned ? 'Unpin activity' : 'Pin to top'}
                aria-pressed={Boolean(activity.pinned)}
                className={classNames({ 'activity-row__pin--active': activity.pinned })}
                icon={faThumbtack}
                size="sm"
                tooltip={activity.pinned ? 'Unpin' : 'Pin to top'}
                variant="ghost"
                onClick={() => onTogglePin?.(activity)}
              />
            )}
            {menu}
          </div>
        )}

        {modals}
      </article>
    );
  }

  return (
    <div className="deal-activity gap-3 d-flex align-self-stretch">
      <div className="timeline">
        <Badge
          bg="light"
          className={classNames('rounded-circle p-2 position-relative', needsReply ? 'needs-reply' : 'border')}
          style={{ zIndex: 1 }}
        >
          <FontAwesomeIcon color={needsReply ? 'white' : 'black'} icon={icon} />
        </Badge>
      </div>
      <div className="d-flex flex-column gap-2 flex-grow-1 py-2 overflow-hidden">
        <div className="d-flex flex-row gap-2 align-items-center justify-content-between">
          <span className="fs-large fw-semibold text-truncate flex-grow-1">{header}</span>
          <div className="d-flex flex-row gap-2 align-items-center flex-shrink-0">
            <span className="text-muted">{formatDate(activity.date)}</span>
            <Avatars avatars={[]} />
          </div>
        </div>
        {showParents && (
          <ActivityParent
            activity={activity}
            asLink={showParents === 'path'}
            currentParent={currentParent}
            options={options}
          />
        )}
        {activity.isEmailThread
          ? <ActivityEmailThread activity={activity} autoExpand={autoExpandEmails} />
          : <ActivityBody activity={activity} />
        }
        {needsReplyActions}
      </div>
      {menu}
      {modals}
    </div>
  );

}
